import { useEffect, useState } from 'react';
import { usePageParam } from '../../hooks/usePageParam';
import { useQuery } from '@apollo/client';
import { ADMIN_STORAGE_RECALCS_QUERY } from '../../graphql/queries';
import Pagination from '../../components/common/Pagination';
import styles from './StorageRecalcPanel.module.css';

export interface StorageRecalc {
  userId: string; name: string; state: 'queued' | 'running' | 'done' | 'error';
  queuedAt: string; startedAt: string | null; finishedAt: string | null; error: string | null;
  folders: number; totalGb: number; originalGb: number; convertedGb: number;
  added: number; changed: number; removed: number; differed: number; missingFiles: number;
  quotaFixed: number; quotaChangeGb: number;
}

const STATE_LABEL: Record<StorageRecalc['state'], string> = {
  queued: 'Queued', running: 'Reading S3…', done: 'Done', error: 'Failed',
};

const gb = (n: number) => `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })} GB`;
const time = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}) : '');

export const recalcSummary = (r: StorageRecalc) => {
  const parts = [
    `${r.folders} folders, ${gb(r.totalGb)} (originals ${gb(r.originalGb)}, converted ${gb(r.convertedGb)})`,
  ];
  const edits = [r.added && `${r.added} added`, r.changed && `${r.changed} resized`, r.removed && `${r.removed} closed`]
    .filter(Boolean);
  parts.push(edits.length ? `records: ${edits.join(', ')}` : 'records already right');
  if (r.differed) parts.push(`${r.differed} differed from what a conversion reported`);
  if (r.missingFiles) parts.push(`${r.missingFiles} files have nothing in S3`);
  if (r.quotaFixed) {
    const sign = r.quotaChangeGb >= 0 ? '+' : '−';
    parts.push(`Storage Used corrected on ${r.quotaFixed} project${r.quotaFixed === 1 ? '' : 's'} (${sign}${gb(Math.abs(r.quotaChangeGb))})`);
  }
  return parts.join(' · ');
};

const RECALCS_PAGE = 10;

const StorageRecalcPanel = () => {
  const [page, setPage] = usePageParam('recalcPage');
  const { data, previousData, startPolling, stopPolling } = useQuery(ADMIN_STORAGE_RECALCS_QUERY, {
    variables: { page, limit: RECALCS_PAGE },
    fetchPolicy: 'network-only',
  });
  const result = (data ?? previousData)?.adminStorageRecalcs;
  const recalcs = (result?.recalcs ?? []) as StorageRecalc[];
  const total: number = result?.total ?? 0;
  // Counted on the server over every page: the running one may be on no page shown.
  const active: number = result?.active ?? 0;
  const busy = active > 0;

  useEffect(() => {
    if (!busy) return undefined;
    startPolling(3000);
    return () => stopPolling();
  }, [busy, startPolling, stopPolling]);

  if (total === 0) return null;
  return (
    <div className={styles.panel} aria-live="polite">
      <h3 className={styles.title}>
        Storage recalculations
        {busy && <span className={styles.activeCount}>{active.toLocaleString('en-US')} in progress or waiting</span>}
      </h3>
      <p className={styles.note}>
        Each one reads the real size of every folder of the user&apos;s projects from S3 (nothing is
        changed there) and saves it to their storage records. Kept until the server restarts.
      </p>
      <ul className={styles.list}>
        {recalcs.map((r) => (
          <li key={`${r.userId}-${r.queuedAt}`} className={styles.item}>
            <span className={`${styles.chip} ${styles[r.state]}`}>{STATE_LABEL[r.state]}</span>
            <span className={styles.name}>{r.name}</span>
            <span className={styles.detail}>
              {r.state === 'done' && recalcSummary(r)}
              {r.state === 'error' && r.error}
              {r.state === 'queued' && `queued ${time(r.queuedAt)}`}
              {r.state === 'running' && `started ${time(r.startedAt)}`}
            </span>
            {r.state === 'done' && r.finishedAt && <span className={styles.time}>{time(r.finishedAt)}</span>}
          </li>
        ))}
      </ul>
      <Pagination page={page} pageSize={RECALCS_PAGE} total={total} onPageChange={setPage} noun="recalculations" variant="inline" />
    </div>
  );
};

export default StorageRecalcPanel;
